// src/components/errorBoundary/ErrorBoundary.jsx
import React from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import errorTrackingService from '../../services/errorTracking/ErrorTrackingService';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID
    const errorId = `react_error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Track error with error tracking service
    errorTrackingService.trackError({
      type: 'react_error_boundary',
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      severity: 'error',
      errorId,
      context: {
        props: this.props,
        boundary: this.props.name || 'unknown',
      },
    });

    // Dispatch custom event for additional error handling
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('react-error', {
        detail: {
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          errorId,
        },
      }));
    }

    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Log error in development
    if (import.meta.env.MODE === 'development') {
      console.error('React Error Boundary caught an error:', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null, 
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReportBug = () => {
    const crashReport = errorTrackingService.createCrashReport();
    const reportData = {
      errorId: this.state.errorId,
      error: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      crashReport,
    };

    // In a real app, you might send this to a support system
    console.log('Bug report data:', reportData);
    
    // Copy to clipboard for easy sharing
    if (navigator.clipboard) {
      navigator.clipboard.writeText(JSON.stringify(reportData, null, 2));
      alert('Error details copied to clipboard!');
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo, this.handleRetry);
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-slate-800/50 backdrop-blur-lg rounded-xl border border-slate-700/50 p-6 sm:p-8">
            {/* Error Icon */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Oops! Something went wrong
              </h1>
              <p className="text-slate-300">
                We&apos;re sorry, but something unexpected happened. Don&apos;t worry, we&apos;ve been notified and are working on it!
              </p>
            </div>

            {/* Error Details (Development Only) */}
            {import.meta.env.MODE === 'development' && (
              <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-red-400 mb-2">
                  Error Details (Development Mode)
                </h3>
                <div className="text-xs text-slate-300 space-y-2">
                  <p><strong>Error ID:</strong> {this.state.errorId}</p>
                  <p><strong>Message:</strong> {this.state.error?.message}</p>
                  {this.state.error?.stack && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-slate-400 hover:text-white">
                        Stack Trace
                      </summary>
                      <pre className="mt-2 text-xs text-slate-400 whitespace-pre-wrap">
                        {this.state.error.stack}
                      </pre>
                    </details>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-slate-400 hover:text-white">
                        Component Stack
                      </summary>
                      <pre className="mt-2 text-xs text-slate-400 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="flex items-center justify-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors"
              >
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </button>
              
              {import.meta.env.MODE === 'development' && (
                <button
                  onClick={this.handleReportBug}
                  className="flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <Bug className="w-4 h-4 mr-2" />
                  Report Bug
                </button>
              )}
            </div>

            {/* Error ID for Support */}
            {this.state.errorId && (
              <div className="mt-6 text-center">
                <p className="text-xs text-slate-400">
                  Error ID: <span className="font-mono text-slate-300">{this.state.errorId}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Share this ID with support if you need help
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;